import React, { type ReactNode } from 'react';
import { Check, CaretDown, FloppyDisk } from '@phosphor-icons/react';
import {
  MenuTrigger,
  Menu,
  MenuItem,
  MenuSeparator,
  MenuSection,
  MenuHeader,
  Text,
  Flex,
  Button,
} from '@chatsdet/ui';
import type {
  DraftSnapshotSummary,
  SnapshotSummary,
  WorkflowRevisionSummary,
} from './hooks/useWorkflowSnapshots';
import { formatUpdatedAt } from './utils';
import {
  buildLegacyPublishedVersionEntries,
  buildRevisionHistoryEntries,
  formatDraftVersionLabel,
  formatPublishedVersionLabel,
  MAX_CHECKPOINT_ENTRIES,
} from './versionHistory';

export type VersionSelection =
  | { type: 'current' }
  | { type: 'snapshot'; version: number }
  | { type: 'revision'; revision: number }
  | { type: 'draft-snapshot'; version: string; basePublishedVersion: number };

type VersionDropdownProps = {
  snapshots: SnapshotSummary[];
  revisions: WorkflowRevisionSummary[];
  draftSnapshots: DraftSnapshotSummary[];
  previewingVersion: VersionSelection | null;
  onSelect: (selection: VersionSelection) => void;
  onSaveVersion?: () => void;
};

type VersionMenuRowProps = {
  id: string;
  isSelected: boolean;
  primary: ReactNode;
  secondary: ReactNode;
};

/** Shared row shape for numbered revisions, date-versioned checkpoints, and legacy published versions. */
function VersionMenuRow({ id, isSelected, primary, secondary }: VersionMenuRowProps) {
  return (
    <MenuItem id={id}>
      <Flex align="center" gap="sm" className="w-full">
        <span className="w-4 shrink-0">
          {isSelected && <Check size={14} weight="bold" className="text-primary" />}
        </span>
        <Flex direction="column" className="flex-1 min-w-0">
          <Text size="sm">{primary}</Text>
          <Text size="xs" className="text-muted-foreground truncate">
            {secondary}
          </Text>
        </Flex>
      </Flex>
    </MenuItem>
  );
}

export function VersionDropdown({
  snapshots,
  revisions,
  draftSnapshots,
  previewingVersion,
  onSelect,
  onSaveVersion,
}: VersionDropdownProps) {
  const isViewingCurrent = previewingVersion === null;
  const hasLegacyPublishedVersions = snapshots.length > 0;
  const hasRevisions = revisions.length > 0;
  const hasCheckpoints = draftSnapshots.length > 0;
  const hasRevisionHistory = hasRevisions || hasCheckpoints;
  const legacyPublishedEntries = buildLegacyPublishedVersionEntries({ snapshots });
  const { entries: revisionHistoryEntries, truncatedCheckpointCount } = buildRevisionHistoryEntries(
    { revisions, draftSnapshots }
  );

  if (!hasLegacyPublishedVersions && !hasRevisionHistory && !onSaveVersion) {
    return (
      <Text size="xs" className="text-muted-foreground">
        Current definition
      </Text>
    );
  }

  const handleAction = (key: React.Key) => {
    const keyString = String(key);
    if (keyString === 'current') {
      onSelect({ type: 'current' });
    } else if (keyString === 'save-version') {
      onSaveVersion?.();
    } else if (keyString.startsWith('revision:')) {
      const revision = Number(keyString.slice('revision:'.length));
      if (Number.isInteger(revision) && revision > 0) onSelect({ type: 'revision', revision });
    } else if (keyString.startsWith('legacy:')) {
      const version = Number(keyString.slice('legacy:'.length));
      if (Number.isInteger(version) && version > 0) onSelect({ type: 'snapshot', version });
    } else if (keyString.startsWith('draft:')) {
      const version = keyString.slice('draft:'.length);
      const checkpoint = draftSnapshots.find((item) => item.version === version);
      if (checkpoint) {
        onSelect({
          type: 'draft-snapshot',
          version,
          basePublishedVersion: checkpoint.basePublishedVersion,
        });
      }
    }
  };

  let triggerLabel = 'Current definition';
  if (previewingVersion?.type === 'snapshot') {
    const snapshot = snapshots.find((item) => item.version === previewingVersion.version);
    const versionLabel = formatPublishedVersionLabel(previewingVersion.version);
    triggerLabel = snapshot?.name
      ? `Viewing ${versionLabel} — ${snapshot.name}`
      : `Viewing ${versionLabel}`;
  } else if (previewingVersion?.type === 'revision') {
    const revision = revisions.find((item) => item.number === previewingVersion.revision);
    triggerLabel = revision?.name
      ? `Viewing Revision ${previewingVersion.revision} — ${revision.name}`
      : `Viewing Revision ${previewingVersion.revision}`;
  } else if (previewingVersion?.type === 'draft-snapshot') {
    const checkpoint = draftSnapshots.find((item) => item.version === previewingVersion.version);
    const versionLabel = formatDraftVersionLabel(
      previewingVersion.basePublishedVersion,
      previewingVersion.version
    );
    triggerLabel = checkpoint?.name
      ? `Viewing ${versionLabel} — ${checkpoint.name}`
      : `Viewing ${versionLabel}`;
  }

  return (
    <MenuTrigger>
      <Button
        variant="ghost"
        size="sm"
        className="px-1.5 py-0.5 text-muted-foreground hover:text-foreground"
      >
        <Flex align="center" gap="xs">
          {triggerLabel}
          <CaretDown size={12} />
        </Flex>
      </Button>
      <Menu className="min-w-[240px] max-h-[400px] overflow-y-auto" onAction={handleAction}>
        <MenuSection>
          <MenuHeader>Current definition</MenuHeader>
          <MenuItem id="current">
            <Flex align="center" gap="sm" className="w-full">
              <span className="w-4 shrink-0">
                {isViewingCurrent && <Check size={14} weight="bold" className="text-primary" />}
              </span>
              <Text size="sm">Current definition</Text>
            </Flex>
          </MenuItem>
        </MenuSection>

        {(hasRevisionHistory || onSaveVersion) && (
          <>
            <MenuSeparator />
            <MenuSection>
              <MenuHeader>
                <Flex align="center" justify="between" gap="sm">
                  <span>Revision history</span>
                  {truncatedCheckpointCount > 0 && (
                    <span className="font-normal normal-case tracking-normal">
                      Showing the {MAX_CHECKPOINT_ENTRIES} most recent checkpoints
                    </span>
                  )}
                </Flex>
              </MenuHeader>
              {revisionHistoryEntries.map((entry) => {
                if (entry.kind === 'revision') {
                  const revision = entry.revision;
                  const isSelected =
                    previewingVersion?.type === 'revision' &&
                    previewingVersion.revision === revision.number;
                  const summary = revision.provenance?.summary;
                  const actor = revision.provenance?.actor;
                  return (
                    <VersionMenuRow
                      key={entry.id}
                      id={entry.id}
                      isSelected={isSelected}
                      primary={
                        <span className="tabular-nums">
                          Revision {revision.number}
                          {revision.name && (
                            <span className="text-muted-foreground ml-1.5">— {revision.name}</span>
                          )}
                        </span>
                      }
                      secondary={`${summary ?? (actor?.kind ? `Saved by ${actor.kind}` : 'Saved revision')} · ${formatUpdatedAt(revision.createdAt)}`}
                    />
                  );
                }

                const checkpoint = entry.checkpoint;
                const isSelected =
                  previewingVersion?.type === 'draft-snapshot' &&
                  previewingVersion.version === checkpoint.version;
                return (
                  <VersionMenuRow
                    key={entry.id}
                    id={entry.id}
                    isSelected={isSelected}
                    primary={
                      <>
                        <span className="font-mono">{entry.versionLabel}</span>
                        {checkpoint.name && (
                          <span className="text-muted-foreground ml-1.5">— {checkpoint.name}</span>
                        )}
                      </>
                    }
                    secondary={formatUpdatedAt(checkpoint.createdAt)}
                  />
                );
              })}
              {onSaveVersion && (
                <>
                  {hasRevisionHistory && <MenuSeparator />}
                  <MenuItem id="save-version">
                    <Flex align="center" gap="sm" className="w-full">
                      <FloppyDisk size={14} className="text-muted-foreground" />
                      <Text size="sm">Save Revision...</Text>
                    </Flex>
                  </MenuItem>
                </>
              )}
            </MenuSection>
          </>
        )}

        {hasLegacyPublishedVersions && (
          <>
            <MenuSeparator />
            <MenuSection>
              <MenuHeader>Legacy published versions</MenuHeader>
              {legacyPublishedEntries.map((entry) => {
                const isSelected =
                  previewingVersion?.type === 'snapshot' &&
                  previewingVersion.version === entry.version;
                return (
                  <VersionMenuRow
                    key={entry.id}
                    id={entry.id}
                    isSelected={isSelected}
                    primary={
                      <>
                        {entry.versionLabel}
                        {entry.name && (
                          <span className="text-muted-foreground ml-1.5">— {entry.name}</span>
                        )}
                      </>
                    }
                    secondary={formatUpdatedAt(entry.timestamp)}
                  />
                );
              })}
            </MenuSection>
          </>
        )}
      </Menu>
    </MenuTrigger>
  );
}
